import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import MaskedReveal from "@/components/site/MaskedReveal";
import Stars from "@/components/site/Stars";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = ["XS", "S", "M", "L", "XL"];

export default function Register() {
  const [email, setEmail] = useState("");
  const [size, setSize] = useState("M");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/register/count`)
      .then((r) => setCount(r.data.count))
      .catch(() => {});
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/register`, { email, size, source: "capsule-01" });
      setDone(true);
      toast.success("You are on the list. Find your purpose.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="register"
      data-testid="register-section"
      className="relative py-32 md:py-48 px-6 md:px-10 bg-black text-white overflow-hidden"
    >
      {/* Deep space base */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_40%,#0d0d0d_0%,#050505_60%,#000_100%)]"
      />

      {/* Starfield */}
      <div aria-hidden className="absolute inset-0">
        <Stars density={1.2} drift={0.015} />
      </div>

      {/* Giant background X */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.06 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.4 }}
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <span className="x-mark text-[80vw] md:text-[52vw] leading-none">X</span>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="flex items-baseline justify-between mb-12 md:mb-16">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            § Register — the orbit is finite
          </span>
          <span
            data-testid="register-count"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50"
          >
            {count !== null ? `${count} in orbit` : "—"}
          </span>
        </div>

        <MaskedReveal
          as="h2"
          trigger="view"
          className="font-display text-6xl md:text-[9rem] uppercase leading-[0.85] tracking-[-0.04em]"
          lines={["Leave a name.", "Find your purpose."]}
        />

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5 text-white/70 text-base leading-relaxed">
            <p>
              R.I.V.H™ releases in numbered capsules. To receive access, leave
              your address below. Doors open by invitation — one small orbit at
              a time.
            </p>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">
              No spam. One signal per capsule.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="md:col-span-6 md:col-start-7 flex flex-col gap-8"
            data-testid="register-form"
          >
            <div>
              <label
                htmlFor="rivh-email"
                className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 block"
              >
                Email
              </label>
              <input
                id="rivh-email"
                data-testid="register-email"
                type="email"
                required
                disabled={done}
                placeholder="you@address.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-line"
              />
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3 block">
                Preferred size
              </div>
              <div className="flex gap-3 flex-wrap">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`register-size-${s}`}
                    onClick={() => setSize(s)}
                    disabled={done}
                    className={`px-5 py-2.5 border font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                      size === s
                        ? "bg-white text-black border-white"
                        : "border-white/25 text-white/70 hover:border-white hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || done}
              className="btn-sharp self-start mt-4"
              data-testid="register-submit"
            >
              <span>
                {done
                  ? "You are in orbit ✦"
                  : loading
                    ? "Opening the door…"
                    : "Enter The List →"}
              </span>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
